/* The result panel after submitting (see result.tsx), under the target on the right. */

.result {
  top: calc(16px + var(--target-head) + var(--preview-size) + 12px);
  right: 16px;
  width: 284px;
  max-height: calc(100dvh - 16px - var(--target-head) - var(--preview-size) - 12px - 16px);
  overflow-y: auto;
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: rise-in 0.35s cubic-bezier(0.3, 0.7, 0.4, 1.2);
}

:global(.shelf-open) .result {
  display: none;
}

@keyframes rise-in {
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.96);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

.head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rank {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--accent);
  color: #fff;
  font-size: 26px;
  font-weight: 700;
  flex: none;
}

.rank[data-rank="S"],
.rank[data-rank="SS"] {
  background: linear-gradient(135deg, #f7c948, #e8962e);
  box-shadow: 0 0 0 3px rgba(247, 201, 72, 0.35);
}

.rank[data-rank="SS"] {
  font-size: 22px;
}

.rank[data-rank="B"],
.rank[data-rank="C"],
.rank[data-rank="D"] {
  background: var(--mint);
}

.total {
  margin: 0;
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.total small {
  margin-left: 0.25em;
  font-size: 13px;
  color: var(--ink-soft);
}

.breakdown {
  margin: 3px 0 0;
  font-size: 11px;
  color: var(--ink-soft);
}

.title {
  margin: 2px 0 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--accent);
}

.groupTitle {
  margin: 2px 0 -4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--ink-soft);
}

.criteria {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 8px;
}

.criterionHead {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 700;
}

.criterionHead small {
  margin-left: 0.3em;
  font-weight: 400;
  color: var(--ink-soft);
}

.meter {
  height: 6px;
  margin-top: 3px;
  border-radius: 99px;
  background: rgba(122, 86, 68, 0.12);
  overflow: hidden;
}

.fill {
  height: 100%;
  width: 0;
  border-radius: inherit;
  background: linear-gradient(90deg, #f3b38c, var(--accent));
}

.bonus .fill {
  background: linear-gradient(90deg, #f7d77a, #eaa93a);
}

.comment {
  margin: 3px 0 0;
  font-size: 11px;
  color: var(--ink-soft);
}

/* The air the letter hopes for, apart from the points (see AtmosphereRow). */
.atmosphere .fill {
  background: linear-gradient(90deg, #c9b8ff, #9a82e0);
}

.atmosphereFoot {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 3px;
}

.atmosphereNote {
  flex: 1;
  margin: 0;
  font-size: 11px;
  line-height: 1.4;
  color: var(--ink-soft);
}

.look {
  flex: none;
  padding: 4px 12px;
  border: none;
  border-radius: 99px;
  background: rgba(154, 130, 224, 0.16);
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  color: #7a62c4;
  cursor: pointer;
}

.look:hover {
  background: rgba(154, 130, 224, 0.26);
}

/* How a piece kneaded freely comes across (see TagsRow): its labels, coloured by kind (the
   air as the letter's air, what it looks like, its shape), filled when put on strongly and
   only outlined when faintly. */
.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
}

.tag {
  --tag: #9a82e0;
  --tag-soft: rgba(154, 130, 224, 0.16);
  --tag-ink: #7a62c4;
  padding: 3px 10px;
  border-radius: 99px;
  background: var(--tag-soft);
  font-size: 12px;
  font-weight: 700;
  color: var(--tag-ink);
}

.tag[data-kind="look"] {
  --tag: var(--mint);
  --tag-soft: rgba(95, 183, 162, 0.16);
  --tag-ink: #3f8f7c;
}

.tag[data-kind="form"] {
  --tag: var(--accent);
  --tag-soft: rgba(229, 138, 92, 0.16);
  --tag-ink: #c0643a;
}

.tag[data-level="2"] {
  background: var(--tag);
  color: #fff;
}

.tag[data-level="0"] {
  background: none;
  box-shadow: inset 0 0 0 1px var(--tag);
}

.tag small {
  margin-right: 0.2em;
  font-size: 10px;
}

.good,
.hint {
  margin: 0;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.5;
}

.good {
  background: var(--accent-soft);
}

.hint {
  background: rgba(95, 183, 162, 0.14);
}

.morph,
.timeline {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.morph input,
.timeline input {
  flex: 1;
  min-width: 0;
}

.morph {
  background: rgba(95, 183, 162, 0.1);
}

.morph span:first-child {
  color: var(--accent);
}

.morph span:last-child {
  color: var(--mint);
}

.morph input {
  accent-color: var(--mint);
}

.timeline {
  background: var(--accent-soft);
}

.timeline span {
  color: var(--accent);
}

.timeline span:last-child {
  min-width: 3.5em;
  text-align: right;
}

.timeline input {
  accent-color: var(--accent);
}

.check {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
}

.check input {
  accent-color: var(--mint);
}

.actions button {
  flex: 1;
}

/* A piece kneaded freely: how to finish it, in place of the judgement (and of the target). */
.finish {
  top: 16px;
}

.finishTitle {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  text-align: center;
  color: var(--accent);
}

/* The finishes, one above another. */
.finishes {
  display: grid;
  gap: 8px;
}

.finishLead {
  margin: -6px 0 2px;
  font-size: 13px;
  text-align: center;
  color: var(--ink-soft);
}

/* Under the finishes: more come with the requests cleared. */
.finishNote {
  margin: 0;
  font-size: 12px;
  text-align: center;
  color: var(--ink-soft);
}

/* How the kitten saw the piece (see TagsRow), boxed apart from how to finish it. */
.seen {
  padding: 8px 10px 10px;
  border-radius: 10px;
  background: rgba(154, 130, 224, 0.08);
}

/*
 * Room for the look's box with its labels in three rows (taller than its other states), so the
 * finishes under it stay put as it loads, looks and labels. Only the rare labels all said
 * strongly at their longest take a fourth row.
 */
.seenSlot {
  min-height: 102px;
}

.seenSlot + .finishLead {
  margin-top: 0;
}

@media (max-width: 640px) {
  .result {
    top: auto;
    left: 10px;
    right: 10px;
    bottom: 10px;
    width: auto;
    max-height: 48dvh;
    gap: 8px;
    padding: 12px 14px 14px;
  }

  .criteria {
    grid-template-columns: 1fr 1fr;
    gap: 6px 14px;
  }

  .comment {
    display: none;
  }
}

.challenge {
  padding: 10px;
  border-radius: 12px;
  background: var(--accent-soft);
  font-size: 12px;
  line-height: 1.5;
}
.challenge p {
  margin: 6px 0 0;
}
